import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import JobMatches from "@/pages/company/JobMatches";
import JobApplicants from "@/pages/company/JobApplicants";
import { ArrowLeft, Link as LinkIcon, FileText, PenLine, Sparkles, Loader2, Save, Wand2, Globe2 } from "lucide-react";

const EMPTY = { title: "", company: "", location: "", employment_type: "", experience: "",
  education: "", salary: "", source_url: "", description: "",
  required_skills: [], preferred_skills: [], languages: [], responsibilities: [],
  visibility: "company_only", active: true };

const toLines = (a) => (Array.isArray(a) ? a.join("\n") : (a || ""));
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function JobEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [mode, setMode] = useState("url"); // url | text | manual
  const [urlVal, setUrlVal] = useState("");
  const [textVal, setTextVal] = useState("");
  const [job, setJob] = useState(isNew ? null : undefined); // null=not extracted yet, undefined=loading
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) api.get(`/company/jobs/${id}`).then((r) => setJob(r.data)).catch(() => { toast.error("Ponuka sa nenašla"); nav("/company/jobs"); });
  }, [id]); // eslint-disable-line

  const extract = async () => {
    setExtracting(true);
    try {
      const payload = mode === "url" ? { url: urlVal } : { text: textVal };
      const { data } = await api.post("/company/jobs/extract", payload);
      setJob({ ...EMPTY, ...data.job });
      toast.success("AI vytvorila návrh ponuky — skontrolujte a uložte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setExtracting(false); }
  };

  const save = async () => {
    if (!job?.title?.trim()) { toast.error("Zadajte názov pozície"); return; }
    setSaving(true);
    try {
      if (isNew) { await api.post("/company/jobs", job); toast.success("Ponuka vytvorená"); }
      else { await api.put(`/company/jobs/${id}`, job); toast.success("Ponuka uložená"); }
      nav("/company/jobs");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setJob((j) => ({ ...j, [k]: v }));

  if (job === undefined) return <CompanyShell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div></CompanyShell>;

  return (
    <CompanyShell>
      <button onClick={() => nav("/company/jobs")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Ponuky</button>
      <h1 className="font-head text-3xl font-bold tracking-tight">{isNew ? "Nová pracovná ponuka" : "Upraviť ponuku"}</h1>

      {isNew && !job && (
        <div className="mt-6 max-w-2xl">
          <div className="flex gap-2">
            <ModeBtn active={mode === "url"} onClick={() => setMode("url")} icon={LinkIcon} label="Z URL" testId="mode-url" />
            <ModeBtn active={mode === "text"} onClick={() => setMode("text")} icon={FileText} label="Z textu" testId="mode-text" />
            <ModeBtn active={mode === "manual"} onClick={() => { setMode("manual"); setJob({ ...EMPTY }); }} icon={PenLine} label="Ručne" testId="mode-manual" />
          </div>

          {mode === "url" && (
            <div className="mt-5 rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
              <label className="text-sm font-semibold text-slate-700">URL inzerátu (napr. Profesia)</label>
              <input data-testid="job-url-input" value={urlVal} onChange={(e) => setUrlVal(e.target.value)} placeholder="https://www.profesia.sk/praca/..."
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
              <p className="text-xs text-slate-500 mt-2">Ak sa obsah nedá načítať, prepnite na „Z textu" a vložte text ponuky.</p>
              <button data-testid="extract-btn" onClick={extract} disabled={extracting || !urlVal.trim()}
                className="mt-4 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-50">
                {extracting ? <><Loader2 className="animate-spin" size={17} /> AI číta inzerát…</> : <><Wand2 size={17} /> Analyzovať URL</>}
              </button>
            </div>
          )}
          {mode === "text" && (
            <div className="mt-5 rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
              <label className="text-sm font-semibold text-slate-700">Text pracovnej ponuky</label>
              <textarea data-testid="job-text-input" value={textVal} onChange={(e) => setTextVal(e.target.value)} rows={8} placeholder="Vložte celý text inzerátu…"
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
              <button data-testid="extract-btn" onClick={extract} disabled={extracting || textVal.trim().length < 20}
                className="mt-4 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-50">
                {extracting ? <><Loader2 className="animate-spin" size={17} /> AI spracúva…</> : <><Wand2 size={17} /> Vytvoriť z textu</>}
              </button>
            </div>
          )}
        </div>
      )}

      {job && (
        <div className="mt-6 max-w-2xl space-y-4" data-testid="job-form">
          {isNew && <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3"><Sparkles size={16} /> Skontrolujte a upravte návrh, potom uložte.</div>}
          <Row><F label="Názov pozície *" v={job.title} on={(v) => set("title", v)} t="job-title" /><F label="Firma" v={job.company} on={(v) => set("company", v)} t="job-company" /></Row>
          <Row><F label="Miesto" v={job.location} on={(v) => set("location", v)} t="job-location" /><F label="Typ úväzku" v={job.employment_type} on={(v) => set("employment_type", v)} t="job-emptype" /></Row>
          <Row><F label="Prax" v={job.experience} on={(v) => set("experience", v)} /><F label="Vzdelanie" v={job.education} on={(v) => set("education", v)} /></Row>
          <F label="Mzda" v={job.salary} on={(v) => set("salary", v)} />
          <TA label="Povinné zručnosti (každá na nový riadok)" v={toLines(job.required_skills)} on={(v) => set("required_skills", fromLines(v))} t="job-required" />
          <TA label="Zručnosti výhodou" v={toLines(job.preferred_skills)} on={(v) => set("preferred_skills", fromLines(v))} />
          <TA label="Jazyky" v={toLines(job.languages)} on={(v) => set("languages", fromLines(v))} />
          <TA label="Zodpovednosti" v={toLines(job.responsibilities)} on={(v) => set("responsibilities", fromLines(v))} />

          {/* Publikovanie – viditeľnosť v Kandidátskom prístupe */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="job-visibility-section">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><Globe2 size={16} className="text-blue-700" /> Viditeľnosť pracovnej ponuky</div>
            <p className="text-xs text-slate-500 mt-1">Určte, kde bude ponuka dostupná. Neaktívna ponuka sa nezobrazuje v Kandidátskom prístupe.</p>
            <div className="mt-3 space-y-2">
              {[
                { v: "company_only", l: "Iba firemná databáza", d: "Dostupné len vo Firemnom prístupe." },
                { v: "candidate_access", l: "Kandidátsky prístup", d: "Publikované kandidátom." },
                { v: "both", l: "Obe možnosti", d: "Firemný aj Kandidátsky prístup." },
              ].map((o) => (
                <label key={o.v} data-testid={`visibility-${o.v}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${job.visibility === o.v ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <input type="radio" name="visibility" checked={job.visibility === o.v} onChange={() => set("visibility", o.v)} className="mt-0.5 accent-blue-700" />
                  <span><span className="text-sm font-semibold text-slate-800">{o.l}</span><span className="block text-xs text-slate-500">{o.d}</span></span>
                </label>
              ))}
            </div>
            <label className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 cursor-pointer" data-testid="job-active-toggle">
              <span><span className="text-sm font-semibold text-slate-800">Aktívna ponuka</span><span className="block text-xs text-slate-500">Neaktívna zmizne z Kandidátskeho prístupu a z počtu aktuálnych ponúk.</span></span>
              <input type="checkbox" checked={job.active !== false} onChange={(e) => set("active", e.target.checked)} className="h-5 w-5 accent-blue-700" />
            </label>
          </div>

          <button data-testid="save-job-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-3.5 font-semibold transition-colors active:scale-95 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {isNew ? "Uložiť ponuku" : "Uložiť zmeny"}
          </button>
        </div>
      )}

      {!isNew && job && <JobApplicants jobId={id} />}
      {!isNew && job && <JobMatches jobId={id} />}
    </CompanyShell>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${active ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-300 hover:bg-slate-50"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}
const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
function F({ label, v, on, t }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input data-testid={t} value={v || ""} onChange={(e) => on(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
    </label>
  );
}
function TA({ label, v, on, t }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <textarea data-testid={t} value={v || ""} onChange={(e) => on(e.target.value)} rows={3}
        className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
    </label>
  );
}
